import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';

import { Spinner } from 'admin-components/components';
import { CurrencyInput, VoucherInput } from 'components/ui/Inputs';
import ValidationForm from 'components/ui/ValidationForm';
import { isValidSocialMediaReference } from 'utils/validators';

import SplitPayment from '../../SplitPayment';
import { handlePaymentSubmit } from '../../SplitPayment/SplitPayment.utils';
import { Errors, StyledRow } from '../../SplitPayment/SplitPayment.styles';
import { StyledLabel } from '../WriteOffPayment/WriteOffPayment.styles';
import { paymentLinkReasons } from '../../../../../constants/paymentLinkReasons';
import StyledReactSelect from '../../../StyledReactSelect';
import { Column, Row } from '../../../../shared/Layout';
import AlertBox from '../../../AlertBox';
import { bookingStatus } from '../../../../../types/booking';

const SocialMediaPayment = ({
  totalPriceToBePaid,
  onAmountChange,
  currentAmount,
  isOnHold,
  payAndConfirm,
  takeDeposit,
  addPayment,
  reference,
  currency,
  method,
  loading,
  isPutOnHoldWithDeposit,
  isExtrasOnly,
  createPaymentLink,
  intl: { formatMessage },
  havePendingSocialMediaPayment,
  hasHotel,
  isPackage,
  isInAfterSales = false,
  status
}) => {
  const shouldShowCreatePaymentLink = isExtrasOnly || (!hasHotel && !isPackage);
  const reasonOptions = [
    { value: '', label: formatMessage({ id: 'payments.dropdownDefault' }) },
    ...paymentLinkReasons.map((reason) => ({
      value: reason,
      label: reason
    }))
  ];
  const handleReasonChange = (changeField) => (event) => {
    changeField('reasonCode')(event.value);
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <ValidationForm
      fields={{ amount: currentAmount, socialMediaReference: '', reasonCode: '' }}
      validators={{
        amount: (values) => {
          const amount = Number(values.amount);

          return amount !== 0 && amount <= totalPriceToBePaid;
        },
        socialMediaReference: (values) => isValidSocialMediaReference(values.socialMediaReference),
        reasonCode: (values) => (shouldShowCreatePaymentLink ? !!values.reasonCode : true)
      }}
      overrideFields={{ amount: currentAmount }}
      validateInitialFields
    >
      {({ fields, onChange, changeField, invalidInputs, hasInvalidInputs, touchedInputs }) => {
        const handleCreateLink = async (e) => {
          e.preventDefault();

          await createPaymentLink({
            variables: {
              bookingReference: reference,
              amount: parseFloat(fields.amount),
              currency,
              reason: fields.reasonCode
            }
          });
        };

        const handleSubmit = async (e) => {
          e.preventDefault();

          await handlePaymentSubmit({
            cashTendered: fields.amount,
            totalPriceToBePaid,
            payAndConfirm,
            takeDeposit,
            addPayment,
            isOnHold,
            bookingReference: reference,
            currency,
            method,
            reference: fields.socialMediaReference
          });
        };

        const handleChange = (e) => {
          onChange(e);

          if (onAmountChange) {
            onAmountChange(e.target.value);
          }
        };

        const isInputDisabled =
          shouldShowCreatePaymentLink || havePendingSocialMediaPayment ? true : isPutOnHoldWithDeposit ? false : isOnHold;

        return (
          <SplitPayment
            title={<FormattedMessage id="payments.AmountToPayBySocialMedia" />}
            subTitle={<FormattedMessage id="payments.canBeSplit" />}
            label={
              shouldShowCreatePaymentLink ? (
                <FormattedMessage id="payments.createPaymentLink" />
              ) : (
                <FormattedMessage id="payments.recordPayment" />
              )
            }
            loading={loading}
            disabled={shouldShowCreatePaymentLink && !invalidInputs.reasonCode ? false : hasInvalidInputs}
            onSubmit={shouldShowCreatePaymentLink ? handleCreateLink : handleSubmit}
          >
            <CurrencyInput name="amount" value={fields.amount} currency={currency} disabled={isInputDisabled} onChange={handleChange} />
            {!havePendingSocialMediaPayment ? (
              <>
                {!shouldShowCreatePaymentLink ? (
                  <VoucherInput
                    message={<FormattedMessage id="payments.SocialMediaReference" />}
                    name="socialMediaReference"
                    value={fields.socialMediaReference}
                    type="text"
                    onChange={(e) => {
                      onChange(e);
                    }}
                  />
                ) : (
                  <>
                    <Row>
                      <Column span={7}>
                        <StyledLabel>
                          <FormattedMessage id="payments.reasonForPaymentLink" />
                        </StyledLabel>
                        <StyledReactSelect
                          id="payment-link-reason-select"
                          name="reasonCode"
                          value={reasonOptions.find((o) => o.value === fields.reasonCode)}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          menuPortalTarget={document.querySelector('body')}
                          height="24px"
                          data-cy="payment-link-reason"
                          options={reasonOptions}
                          onChange={handleReasonChange(changeField)}
                        ></StyledReactSelect>
                      </Column>
                    </Row>
                    {isInAfterSales || status === bookingStatus.PENDING_CHANGES ? (
                      <AlertBox variant={AlertBox.variants.WARNING}>
                        <FormattedMessage id="payments.createPaymentLink.operation.warning" />
                      </AlertBox>
                    ) : status === bookingStatus.TEMPORARILY_ON_HOLD ? (
                      <AlertBox variant={AlertBox.variants.WARNING}>
                        <FormattedMessage id="payments.createPaymentLink.booking.tempOnHoldWarning" />
                      </AlertBox>
                    ) : status === bookingStatus.ON_HOLD ? (
                      <AlertBox variant={AlertBox.variants.WARNING}>
                        <FormattedMessage id="payments.createPaymentLink.booking.onHoldWarning" />
                      </AlertBox>
                    ) : (
                      <AlertBox variant={AlertBox.variants.WARNING}>
                        <FormattedMessage id="payments.createPaymentLink.booking.warning" />
                      </AlertBox>
                    )}
                  </>
                )}
              </>
            ) : null}
            <Errors>
              {invalidInputs.amount && Number(fields.amount) === 0 && <FormattedMessage id="payments.amountZero" />}
              {invalidInputs.amount && Number(fields.amount) !== 0 && <FormattedMessage id="payments.amountTooLarge" />}
            </Errors>
            <Errors>
              {touchedInputs.socialMediaReference && invalidInputs.socialMediaReference && (
                <FormattedMessage id="payments.referenceInvalid" />
              )}
            </Errors>
            {havePendingSocialMediaPayment ? (
              <StyledRow>
                <Errors>
                  <FormattedMessage id="paymentLink.inProgress" />:
                </Errors>
                <a href={`/manage/bookings/${reference}/payments`}>
                  <FormattedMessage id="paymentsTab.title" />
                </a>
              </StyledRow>
            ) : null}
          </SplitPayment>
        );
      }}
    </ValidationForm>
  );
};

SocialMediaPayment.propTypes = {
  currentAmount: PropTypes.string,
  onAmountChange: PropTypes.func,
  totalPriceToBePaid: PropTypes.number,
  loading: PropTypes.bool,
  isOnHold: PropTypes.bool,
  payAndConfirm: PropTypes.func,
  createPaymentLink: PropTypes.func,
  takeDeposit: PropTypes.func,
  addPayment: PropTypes.func,
  reference: PropTypes.string,
  currency: PropTypes.string,
  method: PropTypes.string,
  isPutOnHoldWithDeposit: PropTypes.bool,
  isExtrasOnly: PropTypes.bool,
  hasHotel: PropTypes.bool,
  isPackage: PropTypes.bool,
  intl: intlShape,
  havePendingSocialMediaPayment: PropTypes.bool,
  isInAfterSales: PropTypes.bool,
  status: PropTypes.string
};

export default SocialMediaPayment;
